import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PageTitle from '../Parts/PageTitle';
import {
  MainBlock,
  BodyBlock,
  SelectBoxWrapper,
  StyleSelectBox,
  UpdateButtonDiv,
  AutoOverflowContainer,
  ContentWrapper,
  SectionWrapper,
  SectionTitle,
  FlexRow,
} from './styles/AccessControlPageStyles';
import ColorSF2 from '../../styles/common/ColorSF2';
import SettingItem from './SettingItem';
import AccessControlRadio from './AccessControlRadio';
import AllowedIPAddresses from './AllowedIPAddresses';
import MailSubscriptionList from './MailSubscriptionList';
import AppButton from '../../styles/common/AppButton';
import useDeviceAuthSettings from './hooks/useDeviceAuthSettings';
import getOperators from '../../actions/api/operators/getOperators';
import { SUCCESS_API_GET_ACCESS_CONTROL_OPERATORS } from '../../actions/actionTypes';
import { ROLE } from '../../lib/constants';
import config from '../../config';
import updateDeviceAuthSettings from '../../actions/api/deviceAuth/updateDeviceAuthSettings';
import getDeviceAuthSubscriptionSettings from '../../actions/api/deviceAuth/getDeviceAuthSubscriptionSettings';

const AccessControlPage = () => {
  /**
   * デバイス認証設定の状態と操作関数
   */
  const {
    deviceAuthSettings,
    handleRadioChange,
    handleSubscriptionsChange,
    addedIpAddress,
    removedIpAddress,
    selectedGroup,
  } = useDeviceAuthSettings();

  const [selectedOption, setSelectedOption] = useState('1');

  // グループ選択関係
  const apiToken = useSelector(state => state.main.apiToken);
  const dispatch = useDispatch();
  const groupItems = useSelector(state => state.group.groups);
  const groups = useSelector(state => state.group.groups);
  const defaultGroupId = groups.find(group => group.isDefault)?.id;

  /**
   * グループ変更時の処理。グループに所属するユーザーと通知先ユーザーを取得する
   */
  const handleGroupChange = async e => {
    const selectedGroupId = e.target.value;
    if (selectedGroupId === null) {
      return;
    }

    selectedGroup(e.target.value);

    const getOperatorsParams = {
      accountStatuses: config.searchAccountStatus.enable,
      roles: [ROLE.ADMIN, ROLE.COMPANY_ADMIN],
      groupIds: [selectedGroupId],
    };

    await dispatch(
      getOperators(
        apiToken,
        getOperatorsParams,
        SUCCESS_API_GET_ACCESS_CONTROL_OPERATORS,
      ),
    );
    await dispatch(getDeviceAuthSubscriptionSettings(apiToken, e.target.value));
  };

  const handleUpdateButtonClick = () => {
    dispatch(updateDeviceAuthSettings(apiToken, deviceAuthSettings));
  };

  return (
    <MainBlock>
      <BodyBlock>
        <PageTitle title="アクセス制御" />
        <AutoOverflowContainer>
          <ContentWrapper>
            <SectionWrapper>
              <SectionTitle>企業設定</SectionTitle>
              <SettingItem title="アクセス制御">
                <FlexRow>
                  <AccessControlRadio
                    labelText="ON"
                    isEnabled={deviceAuthSettings.isEnabled}
                    onRadioChange={() => handleRadioChange(true)}
                  />
                  <AccessControlRadio
                    labelText="OFF"
                    isEnabled={deviceAuthSettings.isEnabled}
                    onRadioChange={() => handleRadioChange(false)}
                  />
                </FlexRow>
              </SettingItem>
              <SettingItem title="許可するIPアドレス" isLastElement="true">
                <AllowedIPAddresses
                  deviceAuthSettings={deviceAuthSettings}
                  addedIpAddress={addedIpAddress}
                  removedIpAddress={removedIpAddress}
                />
              </SettingItem>
            </SectionWrapper>
            <SectionWrapper>
              <SectionTitle>グループ設定</SectionTitle>
              <SettingItem title="グループ選択">
                <SelectBoxWrapper>
                  <StyleSelectBox
                    value={
                      deviceAuthSettings?.subscriptions?.groupId ||
                      defaultGroupId
                    }
                    onChange={handleGroupChange}
                  >
                    {groupItems.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </StyleSelectBox>
                </SelectBoxWrapper>
              </SettingItem>
              <SettingItem
                title="認証コード、アラートメール送信先"
                isLastElement="true"
              >
                <SelectBoxWrapper>
                  <StyleSelectBox
                    onChange={e => setSelectedOption(e.target.value)}
                  >
                    <option value="1">
                      <FormattedMessage id="accessControlPage.roleFilterSelectBox.option.allAdmin" />
                    </option>
                    <option value="2">
                      <FormattedMessage id="accessControlPage.roleFilterSelectBox.option.companyAdmin" />
                    </option>
                    <option value="3">
                      <FormattedMessage id="accessControlPage.roleFilterSelectBox.option.admin" />
                    </option>
                  </StyleSelectBox>
                </SelectBoxWrapper>
                <MailSubscriptionList
                  selectedOption={selectedOption}
                  deviceAuthSettings={deviceAuthSettings}
                  onSubscriptionsChange={handleSubscriptionsChange}
                />
              </SettingItem>
            </SectionWrapper>
            <UpdateButtonDiv>
              <AppButton
                type="button"
                bgColor={ColorSF2.navyBlue}
                bold
                width="64px"
                height="36px"
                onClick={handleUpdateButtonClick}
              >
                <FormattedMessage id="common.button.update" />
              </AppButton>
            </UpdateButtonDiv>
          </ContentWrapper>
        </AutoOverflowContainer>
      </BodyBlock>
    </MainBlock>
  );
};

export default AccessControlPage;
